import { Box } from '@mui/material';
import _ from 'lodash';
import qs from 'qs';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ListHeader from '../components/listHeader/ListHeader';
import FilterSidebar from '../components/common/filters/FilterSidebar';
import { FilterState } from '../components/common/filters/interfaces';
import PreferencePill from '../components/preferences/PreferencePill';
import { useDownloadCsv } from '../hooks/useDownloadCsv';
import { useIsMobile } from '../hooks/useIsMobile';
import { useSearchTerm } from '../hooks/useSearchTerm';
import { useSyncPageParams } from '../hooks/useSyncPageParams';
import { useUserSorts } from '../hooks/useUserSorts';
import { AccountAvailableColumns, useGetAllAccounts } from '../services/react-query/accounts';
import { useGetAllPreferences } from '../services/react-query/preferences';
import { listPageLayoutStyles, listPageTableWrapperStyles } from '../styles/sxStyling';
import { Account } from '../types/Account';
import { PREFERENCES_VALID_KEYS } from '../types/Preference';
import { UserConfigurableModule } from '../types/User';
import { Loading } from '../uikit';
import ITable, { AvailableColumn } from '../uikit/itable/ITable';
import { AccountColumns, AccountFilters } from '../utility/account';
import ListHeaderActions from '../components/listHeader/ListHeaderActions';
import { useGetAllAccountTemplates } from '../services/react-query/account_templates';
import TemplateModal from '../components/common/TemplateModal/TemplateModal';

const AccountsList: React.FC = () => {
  const navigate = useNavigate();
  const { currentPage, setCurrentPage } = useSyncPageParams();
  const { userSorts } = useUserSorts(UserConfigurableModule.ACCOUNTS);
  const { isMobile } = useIsMobile();

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [filterState, setFilterState] = useState<FilterState>(undefined);
  const [showModal, setShowModal] = useState(false);

  const { data: accountTemplateData } = useGetAllAccountTemplates({
    params: { per_page: 0 },
  });
  const accountTemplates = accountTemplateData?.data || [];

  const { data: prefs } = useGetAllPreferences({
    params: {
      filters: {
        keys: [PREFERENCES_VALID_KEYS.ACCOUNT_TYPE, PREFERENCES_VALID_KEYS.ACCOUNT_STATUS, PREFERENCES_VALID_KEYS.ACCOUNT_SCHOOL_DISTRICT],
      },
    },
  });

  const prefByUuid = useMemo(() => _.keyBy(prefs ?? [], 'uuid'), [prefs]);

  const baseParams = {
    search: useSearchTerm(),
    filters: filterState || {},
    sort: userSorts,
  };

  const { data, isLoading: acctLoading } = useGetAllAccounts({
    params: {
      ...baseParams,
      page: currentPage,
    },
    enabled: true,
  });

  const pillColumns = useMemo(() => {
    return AccountColumns.map<AvailableColumn<Account, AccountAvailableColumns>>((col) => {
      if (col.key === 'status_value') {
        return {
          ...col,
          render: (row: Account) => <PreferencePill {...prefByUuid[row.status_uuid ?? '']} />,
        };
      }
      if (col.key === 'type_value') {
        return {
          ...col,
          render: (row: Account) => <PreferencePill {...prefByUuid[row.type_uuid ?? '']} />,
        };
      }
      if (col.key === 'school_district_value') {
        return {
          ...col,
          render: (row: Account) => <PreferencePill {...prefByUuid[row.school_district_uuid ?? '']} />,
        };
      }
      return col;
    });
  }, [prefByUuid]);

  const { isDownloadingCsv, handleDownloadCsv } = useDownloadCsv({
    params: baseParams,
    controller: UserConfigurableModule.ACCOUNTS,
  });

  if (acctLoading) return <Loading />;

  const newWithTemplate = (templateUuid: string | null) => {
    const params: any = {};
    if (templateUuid) params.template = templateUuid;

    navigate(`./new?${qs.stringify(params)}`);
  };

  return (
    <Box>
      <ListHeader
        title="Accounts"
        filtersOpen={sidebarOpen}
        onToggleFilters={() => setSidebarOpen((o) => !o)}
        rightActions={
          <ListHeaderActions
            module={UserConfigurableModule.ACCOUNTS}
            availableColumns={pillColumns.map(({ key, label }) => ({ key, label }))}
            isDownloadingCsv={isDownloadingCsv}
            onDownloadCsv={handleDownloadCsv}
            onAdd={() => setShowModal(true)}
          />
        }
      />

      <Box sx={listPageLayoutStyles}>
        {sidebarOpen && (
          <FilterSidebar
            moduleFilters={AccountFilters(prefs)}
            open={sidebarOpen}
            module={UserConfigurableModule.ACCOUNTS}
            onClose={() => setSidebarOpen(false)}
            activeFilter={filterState}
            setActiveFilter={setFilterState}
          />
        )}

        <Box sx={listPageTableWrapperStyles}>
          <ITable<Account, AccountAvailableColumns>
            availableColumns={pillColumns}
            data={data?.data || []}
            module={UserConfigurableModule.ACCOUNTS}
            page={currentPage}
            count={data?.meta?.total_pages ?? 1}
            onPageChange={(_e, v) => setCurrentPage(v)}
          />
        </Box>
      </Box>

      <TemplateModal
        isOpen={showModal}
        onCancel={() => setShowModal(false)}
        onCreate={newWithTemplate}
        templates={accountTemplates}
        title="Please choose which account template to use"
      />
    </Box>
  );
};

export default AccountsList;
